import type { UpdateSystem } from "@engine/core/Lifecycle";
import type { Entity } from "../components";
import type { EcsWorld } from "../world";

export interface EntityCollisionOptions {
  /** Spatial hash cell size in px. Should be ~2× the typical entity radius. */
  cellSize?: number;
  /**
   * Mass-weighted resolution. If both entities have `health.max` (proxy for
   * mass) the heavier one moves less. Default true.
   */
  weighted?: boolean;
  /**
   * Optional filter — return false to skip a pair. Useful to allow some kinds
   * to overlap (projectiles, ghosts).
   */
  pairFilter?: (a: Entity, b: Entity) => boolean;
}

/**
 * Pairwise circle-vs-circle collision resolution between dynamic entities.
 *
 * Broadphase: a per-frame spatial hash grouping entities by cell. Each entity
 * tests only against neighbors in the same and adjacent cells.
 *
 * Resolution: when two circles overlap by `pen`, we push them apart along the
 * separating axis by `pen/2` each (or weighted by mass when `weighted=true`).
 *
 * Position is the only thing modified — velocities are preserved so
 * MovementSystem and AI keep their intent. Entities tagged `asleep` are
 * skipped (lets ChunkManager freeze faraway crowds).
 *
 * Cost: O(N · neighbors), typically near O(N) when entities are well-spread
 * across cells.
 */
export class EntityCollisionSystem implements UpdateSystem {
  private query;
  private cellSize: number;
  private weighted: boolean;
  private pairFilter: ((a: Entity, b: Entity) => boolean) | undefined;
  private buckets = new Map<number, Entity[]>();

  constructor(world: EcsWorld, opts: EntityCollisionOptions = {}) {
    this.cellSize = opts.cellSize ?? 32;
    this.weighted = opts.weighted ?? true;
    this.pairFilter = opts.pairFilter;
    this.query = world.with("position", "sprite").without("asleep");
  }

  update(_dt: number): void {
    this.rebuildBuckets();
    for (const [key, list] of this.buckets) {
      const [cx, cy] = unpackKey(key);
      // Test against this and 4 neighbor cells (avoid double-checks)
      this.collideListWith(list, list, true);
      this.collideListWith(list, this.buckets.get(packKey(cx + 1, cy)));
      this.collideListWith(list, this.buckets.get(packKey(cx, cy + 1)));
      this.collideListWith(list, this.buckets.get(packKey(cx + 1, cy + 1)));
      this.collideListWith(list, this.buckets.get(packKey(cx - 1, cy + 1)));
    }
  }

  private rebuildBuckets(): void {
    this.buckets.clear();
    for (const e of this.query) {
      if (!e.position || !e.sprite) continue;
      const cx = Math.floor(e.position.x / this.cellSize);
      const cy = Math.floor(e.position.y / this.cellSize);
      const key = packKey(cx, cy);
      const arr = this.buckets.get(key);
      if (arr) arr.push(e);
      else this.buckets.set(key, [e]);
    }
  }

  private collideListWith(a: Entity[], b: Entity[] | undefined, sameList = false): void {
    if (!b) return;
    for (let i = 0; i < a.length; i++) {
      const ea = a[i]!;
      const startJ = sameList ? i + 1 : 0;
      for (let j = startJ; j < b.length; j++) {
        const eb = b[j]!;
        if (this.pairFilter && !this.pairFilter(ea, eb)) continue;
        this.resolve(ea, eb);
      }
    }
  }

  private resolve(a: Entity, b: Entity): void {
    const pa = a.position;
    const pb = b.position;
    const sa = a.sprite;
    const sb = b.sprite;
    if (!pa || !pb || !sa || !sb) return;
    const ra = sa.radius ?? 0;
    const rb = sb.radius ?? 0;
    if (ra <= 0 || rb <= 0) return;
    const dx = pb.x - pa.x;
    const dy = pb.y - pa.y;
    const distSq = dx * dx + dy * dy;
    const minDist = ra + rb;
    if (distSq >= minDist * minDist) return;

    let dist = Math.sqrt(distSq);
    let nx: number;
    let ny: number;
    if (dist === 0) {
      // Identical positions: shove along an arbitrary axis.
      nx = 1;
      ny = 0;
      dist = 0.0001;
    } else {
      nx = dx / dist;
      ny = dy / dist;
    }
    const pen = minDist - dist;

    let pushA = 0.5;
    let pushB = 0.5;
    if (this.weighted) {
      const ma = a.health?.max ?? 1;
      const mb = b.health?.max ?? 1;
      const total = ma + mb;
      pushA = mb / total;
      pushB = ma / total;
    }
    pa.x -= nx * pen * pushA;
    pa.y -= ny * pen * pushA;
    pb.x += nx * pen * pushB;
    pb.y += ny * pen * pushB;
  }
}

// 16-bit signed pack — supports cells in [-32k, +32k] => world up to ~1M px
// (cellSize 32). Plenty for a chunked seamless world.
const KEY_BIAS = 32768;
const packKey = (cx: number, cy: number): number => (cx + KEY_BIAS) * 65536 + (cy + KEY_BIAS);
const unpackKey = (k: number): [number, number] => {
  const cy = (k % 65536) - KEY_BIAS;
  const cx = Math.floor(k / 65536) - KEY_BIAS;
  return [cx, cy];
};
